import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSION_NAV_SECTIONS, ROLE_DASHBOARD_PATHS, APP_NAME, APP_SUBTITLE, NavItem, NavSection } from '../../../utils/constants';
import { Icons } from '../Icons';
import omLogo from '../../../images/images.jpg';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const { user, userRole } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();

  // Filter navigation items based on user permissions
  const filteredSections = useMemo(() => {
    if (!user) return [];

    const sections: NavSection[] = [];

    for (const section of PERMISSION_NAV_SECTIONS) {
      const filteredItems: NavItem[] = [];

      for (const item of section.items) {
        // Dashboard is always visible
        if (item.path === '/dashboard') {
          // Replace with role-specific dashboard path
          const dashboardPath = userRole ? ROLE_DASHBOARD_PATHS[userRole] : '/dashboard/employee';
          filteredItems.push({ ...item, path: dashboardPath });
          continue;
        }

        // Check single permission
        if (item.permission && !hasPermission(item.permission)) {
          continue;
        }

        // Check any permission
        if (item.anyPermission && item.anyPermission.length > 0 && !hasAnyPermission(item.anyPermission)) {
          continue;
        }

        filteredItems.push(item);
      }

      // Only add section if it has items
      if (filteredItems.length > 0) {
        sections.push({
          title: section.title,
          items: filteredItems,
        });
      }
    }

    return sections;
  }, [user, userRole, hasPermission, hasAnyPermission]);

  if (!user || !userRole) return null;

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              <img src={omLogo} alt="Logo" />
            </div>
            {isOpen && (
              <div className="logo-text-container">
                <span className="logo-text">{APP_NAME}</span>
                <span className="logo-subtitle">{APP_SUBTITLE}</span>
              </div>
            )}
          </div>
        </div>

        {/* Toggle button positioned at the edge */}
        <button className="sidebar-toggle-btn" onClick={onToggle} title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          {isOpen ? <Icons.ChevronLeft /> : <Icons.ChevronRight />}
        </button>

        <nav className="sidebar-nav">
          {filteredSections.map((section, sectionIndex) => (
            <div key={section.title} className="nav-section">
              {isOpen && section.title && (
                <div className="nav-section-title">{section.title}</div>
              )}
              {!isOpen && sectionIndex > 0 && <div className="nav-section-divider" />}
              <div className="nav-section-items">
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end
                    className={({ isActive }) =>
                      `nav-item ${isActive ? 'nav-item-active' : ''}`
                    }
                    title={!isOpen ? item.label : undefined}
                  >
                    <span className="nav-icon">
                      <Icons name={item.icon} />
                    </span>
                    {isOpen && <span className="nav-label">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
      {isOpen && <div className="sidebar-overlay" onClick={onToggle} />}
    </>
  );
}

export default Sidebar;
